import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useFieldArray, useForm } from 'react-hook-form';
import type { Path, UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { DiagnosisResponseType } from 'types/api/diagnosisResponseType';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import {
  buildEditFormFromResponse,
  buildInitialDiagnosisForm,
  convertFormValuesToParam,
} from 'pages/ws/diagnosisEdit/utils/diagnosisFormConverter';
import {
  collectRequiredErrors,
  validateRespondentMailDivRequired,
} from 'pages/ws/diagnosisEdit/utils/diagnosisValidation';
import { submit } from 'utils/app';
import { openPreviewUrl } from 'utils/diagnosis';
import { getProductionUrl } from 'utils/terms';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

/**
 * useWsDiagnosisEdit の戻り値型。
 */
export interface UseWsDiagnosisEditResult {
  useFormMethods: UseFormReturn<DiagnosisEditFormStateType>;
  questionFieldArray: UseFieldArrayReturn<DiagnosisEditFormStateType, 'questions', 'id'>;
  resultFieldArray: UseFieldArrayReturn<DiagnosisEditFormStateType, 'results', 'id'>;
  respondentFieldArray: UseFieldArrayReturn<DiagnosisEditFormStateType, 'respondentFields', 'id'>;
  initialized: boolean;
  processingOnSave: boolean;
  processingOnPreview: boolean;
  processingOnPublish: boolean;
  processingOnPublishTerminate: boolean;
  processingOnReserveCancel: boolean;
  processingOnDelete: boolean;
  processingOnCreateQuestion: boolean;
  diagnosisState: DiagnosisResponseType | null;
  isExpiredPlan: boolean;
  canPublished: boolean;
  isUnpublished: () => boolean;
  isReadOnly: () => boolean;
  modalPublishOpenState: boolean;
  setModalPublishOpenState: (open: boolean) => void;
  modalConfirmOpenState: boolean;
  setModalConfirmOpenState: (open: boolean) => void;
  modalConfirmTitle: string;
  onSave: () => Promise<void>;
  onPreview: () => Promise<void>;
  onPublishModalOpen: () => Promise<void>;
  onConfirmModalSubmit: () => void;
  onPublishModalSubmit: (attr: {
    publishStartAt: number | null;
    publishEndAt: number | null;
    noIndexFlag: boolean;
  }) => Promise<void>;
  onPublishTerminate: () => Promise<void>;
  onReserveCancel: () => Promise<void>;
  onDelete: () => Promise<void>;
  onCreateDiagnosisRef: React.MutableRefObject<(() => void) | undefined>;
  navigate: ReturnType<typeof useNavigate>;
  confirm: ReturnType<typeof useAppState>['confirm'];
}

// ------------------------------------------------------------------ //
// hook
// ------------------------------------------------------------------ //

/**
 * 診断型コンテンツ作成・編集画面のカスタムフック。
 * すべての状態・副作用・APIコール・フォーム初期化を集約する。
 */
export function useWsDiagnosisEdit(): UseWsDiagnosisEditResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { diagnosisCd } = useParams<{ diagnosisCd: string }>();

  const [acctState] = useAtom(AcctState);

  const { updateAcctState, noticeMessageSuccess, noticeMessageFailure, confirm, canPublish } =
    useAppState();
  const {
    postDiagnosisRef,
    postDiagnosisPublish,
    postDiagnosisPublishTerminate,
    postDiagnosisDelete,
    postDiagnosisUpdate,
    postDiagnosisRegister,
  } = useApiState();

  // ローディングの制御設定
  const [initialized, setInitialized] = useState(false);
  const [processingOnSave, setProcessingOnSave] = useState(false);
  const [processingOnPreview, setProcessingOnPreview] = useState(false);
  const [processingOnPublish, setProcessingOnPublish] = useState(false);
  const [processingOnPublishTerminate, setProcessingOnPublishTerminate] = useState(false);
  const [processingOnReserveCancel, setProcessingOnReserveCancel] = useState(false);
  const [processingOnDelete, setProcessingOnDelete] = useState(false);
  const [processingOnCreateQuestion] = useState(false);

  // 診断情報
  const [diagnosisState, setDiagnosisState] = useState<DiagnosisResponseType | null>(null);
  const [modalPublishOpenState, setModalPublishOpenState] = useState<boolean>(false);
  const [modalConfirmOpenState, setModalConfirmOpenState] = useState<boolean>(false);
  const [modalConfirmTitle, setModalConfirmTitle] = useState<string>('');

  // チャットボットコンポーネントからonCreateDiagnosisを呼び出すためのref
  const onCreateDiagnosisRef = useRef<(() => void) | undefined>(undefined);

  // フォーム設定
  const useFormMethods = useForm<DiagnosisEditFormStateType>();
  const { control, handleSubmit, setValue, getValues, setError } = useFormMethods;
  const questionFieldArray = useFieldArray({
    control,
    name: 'questions',
  });
  const resultFieldArray = useFieldArray({
    control,
    name: 'results',
  });
  const respondentFieldArray = useFieldArray({
    control,
    name: 'respondentFields',
  });

  // バリデーションメッセージ
  const validAlertMessage = '入力に誤りがあります。赤文字で表示されている箇所を確認してください。';
  // プラン（利用期間切れ）
  const isExpiredPlan = acctState.company.plan.isExpired;
  // 公開可能
  const canPublished = canPublish(DivConst.items.contentsType.diagnosis.value);

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        // eslint-disable-next-line react-hooks/immutability -- 初回マウント時にhoisting相当で_initStateを呼ぶ設計
        await _initState();
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時 1 回のみ実行する初期化処理
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 保存ボタン押下時の処理
   */
  const onSave = async () =>
    submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async () => {
        try {
          // 回答者情報の取得項目の整合性検証
          const respondentMailDivError = validateRespondentMailDivRequired(getValues());
          if (respondentMailDivError) {
            setError(respondentMailDivError.name as Path<DiagnosisEditFormStateType>, {
              type: 'custom',
              message: respondentMailDivError.message,
            });
            noticeMessageFailure(validAlertMessage);
            return;
          }

          const response = await _save(
            diagnosisState?.diagnosisStatusDiv || DivConst.items.diagnosisStatus.unpublished.value
          );
          setDiagnosisState(response);

          // 処理完了メッセージの表示
          noticeMessageSuccess('診断型コンテンツの保存が完了しました。');
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /**
   * プレビューボタン押下時の処理
   */
  const onPreview = async () =>
    submit(
      processingOnPreview,
      setProcessingOnPreview,
      handleSubmit(
        async () => {
          try {
            let diagnosis = diagnosisState;

            // 必須検証の追加実施
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }

            // 未稼働の場合は保存処理を実施
            if (_isUnpublished()) {
              // 診断情報の更新
              diagnosis = await _save(DivConst.items.diagnosisStatus.unpublished.value);
              // 診断型コンテンツ公開APIの呼び出し
              diagnosis = await postDiagnosisPublish({
                diagnosisId: diagnosis.id,
                mode: 'preview',
              });
              setDiagnosisState(diagnosis);

              // 保存後に自動でプレビュー画面を開くとポップアップブロック判定されるため、ユーザーにアクションを促す
              confirm({
                title: '診断型コンテンツの保存が完了しました',
                message: `このままプレビュー画面を開きますか？`,
                onSubmit: async () => {
                  openPreviewUrl(diagnosis.previewCd);
                },
              });
            } else {
              // プレビュー画面を開く
              openPreviewUrl(diagnosis.previewCd);
            }
          } catch (e) {
            showBoundary(e);
          }
        },
        // バリデーションエラーがある場合の処理
        () => {
          _validRequiredControllers();
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * 公開ボタン押下時の処理
   */
  const onPublishModalOpen = async () =>
    submit(
      processingOnPublish,
      setProcessingOnPublish,
      handleSubmit(
        async () => {
          try {
            // 未設定項目確認モーダルを開く
            if (!getValues().privacyPolicyUrl) {
              setModalConfirmTitle('プライバシーポリシーが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().termsUrl) {
              setModalConfirmTitle('利用規約が未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().copyright) {
              setModalConfirmTitle('コピーライトが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().reportMailReplyAddress) {
              setModalConfirmTitle('返信元メールアドレスが未登録です');
              setModalConfirmOpenState(true);
              return;
            }

            // 必須検証の追加実施
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }
            // 診断型コンテンツ公開設定モーダルを開く
            setModalPublishOpenState(true);
          } catch (e) {
            showBoundary(e);
          }
        },
        // バリデーションエラーがある場合の処理
        () => {
          _validRequiredControllers();
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * 未設定項目確認モーダルのOKボタンがクリックされた時の処理
   */
  const onConfirmModalSubmit = () => {
    setModalConfirmOpenState(false);
    submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async () => {
        try {
          // 回答者情報の取得項目の整合性検証
          const respondentMailDivError = validateRespondentMailDivRequired(getValues());
          if (respondentMailDivError) {
            setError(respondentMailDivError.name as Path<DiagnosisEditFormStateType>, {
              type: 'custom',
              message: respondentMailDivError.message,
            });
            noticeMessageFailure(validAlertMessage);
            return;
          }

          const response = await _save(
            diagnosisState?.diagnosisStatusDiv || DivConst.items.diagnosisStatus.unpublished.value
          );
          setDiagnosisState(response);
          navigate(`/setting/${acctState.company.cd}/company`);
        } catch (e) {
          showBoundary(e);
        }
      })
    );
  };

  /**
   * 公開設定完了時の処理。
   */
  const onPublishModalSubmit = async (attr: {
    publishStartAt: number | null;
    publishEndAt: number | null;
    noIndexFlag: boolean;
  }) => {
    try {
      // ローディングの表示
      setProcessingOnPublish(true);
      // 固定項目
      setValue('publishStartAt', attr.publishStartAt);
      setValue('publishEndAt', attr.publishEndAt);
      setValue('noIndexFlag', attr.noIndexFlag);

      // 診断情報の更新
      let response = await _save(DivConst.items.diagnosisStatus.reserved.value);

      // 即時公開の場合の処理
      if (attr.publishStartAt == null) {
        // 診断型コンテンツ公開APIの呼び出し
        response = await postDiagnosisPublish({
          diagnosisId: response.id,
          mode: 'production',
        });
        updateAcctState(['company', 'activeContent', 'usage'], acctState.company.cd);
      } else {
        // 予約公開の場合は最新の診断情報を再取得（popupImageKey など含めるため）
        response = await postDiagnosisRef({
          diagnosisCd: response.cd,
        });
      }

      setDiagnosisState(response);
      // ローディングの停止
      setProcessingOnPublish(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 公開停止ボタン押下時の処理
   */
  const onPublishTerminate = async () => {
    try {
      // ローディングの表示
      setProcessingOnPublishTerminate(true);
      // 診断型コンテンツ公開停止APIの呼び出し
      setDiagnosisState(
        await postDiagnosisPublishTerminate({
          diagnosisId: diagnosisState!.id,
          mode: 'production',
        })
      );
      // 画面の状態を更新
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      // ローディングの停止
      setProcessingOnPublishTerminate(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 予約取消ボタン押下時の処理
   */
  const onReserveCancel = async () => {
    try {
      // ローディングの表示
      setProcessingOnReserveCancel(true);
      // 診断型コンテンツ情報の更新
      const response = await _save(DivConst.items.diagnosisStatus.unpublished.value);
      setDiagnosisState(response);
      // ローディングの停止
      setProcessingOnReserveCancel(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 削除ボタン押下時の処理
   */
  const onDelete = async () => {
    try {
      // ローディングの表示
      setProcessingOnDelete(true);
      // 診断情報の更新
      await postDiagnosisDelete({ diagnosisId: diagnosisState!.id });
      // 画面の状態を更新
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      // ローディングの停止
      setProcessingOnDelete(false);
      // コンテンツ画面に遷移
      navigate(`/ws/${acctState.company.cd}/content/search`);
    } catch (e) {
      showBoundary(e);
    }
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の状態を初期化します。
   */
  const _initState = async () => {
    // reportMailSenderAddress は config に依存するため utils に含められない。hook 側で明示的にセットする。
    setValue('reportMailSenderAddress', config.ses.from.address);

    // 新規登録の場合
    if (!diagnosisCd) {
      const initialValues = buildInitialDiagnosisForm(acctState);
      (Object.entries(initialValues) as [keyof DiagnosisEditFormStateType, unknown][]).forEach(
        ([key, value]) => {
          setValue(key, value as DiagnosisEditFormStateType[typeof key]);
        }
      );
      onCreateDiagnosisRef.current?.();
    }
    // 更新の場合
    else {
      const response = await postDiagnosisRef({ diagnosisCd });
      setDiagnosisState(response);

      // buildEditFormFromResponse が含まない acctState / config 由来の固定フィールドをセット
      setValue('privacyPolicyUrl', acctState.company.privacyPolicyUrl ?? '');
      setValue('reportMailReplyAddress', acctState.company.reportMailReplyAddress ?? '');
      setValue('copyright', acctState.company.copyright ?? '');
      setValue(
        'termsUrl',
        acctState.company.termsInputDiv === DivConst.items.termsInputType.url.value
          ? (acctState.company.termsUrl ?? '')
          : getProductionUrl(acctState.company.cd)
      );

      const editValues = buildEditFormFromResponse(response);
      (Object.entries(editValues) as [keyof DiagnosisEditFormStateType, unknown][]).forEach(
        ([key, value]) => {
          setValue(key, value as DiagnosisEditFormStateType[typeof key]);
        }
      );
    }
  };

  /**
   * 診断情報を保存します。
   */
  const _save = async (diagnosisStatusDiv: number): Promise<DiagnosisResponseType> => {
    // eslint-disable-next-line no-useless-assignment -- 後続の条件分岐で上書きされるため初期値としてnullを設定
    let response: DiagnosisResponseType | null = null;
    const commonParam = {
      ...convertFormValuesToParam(getValues()),
      popupImageKey: getValues().popupImageKey,
    };
    // 新規登録の場合
    if (!diagnosisState) {
      response = await postDiagnosisRegister({
        companyId: acctState.company.id,
        diagnosisStatusDiv,
        ...commonParam,
      });
    }
    // 更新の場合
    else {
      response = await postDiagnosisUpdate({
        diagnosisId: diagnosisState.id,
        diagnosisStatusDiv,
        respondentFieldGroupId: diagnosisState.respondentFieldGroupId,
        ...commonParam,
      });
    }
    // 画面の状態を更新
    updateAcctState(['company', 'activeContent'], acctState.company.cd);

    return response!;
  };

  /**
   * 公開時に必須となる項目を検証します。
   */
  const _validRequiredControllers = (): boolean => {
    const values = getValues();
    const errors = collectRequiredErrors(values);
    errors.forEach(error => {
      setError(error.name as Path<DiagnosisEditFormStateType>, {
        type: 'custom',
        message: error.message,
      });
    });
    return errors.length === 0;
  };

  /**
   * 未稼働かどうかを判定します。
   */
  const _isUnpublished = (): boolean => {
    return (
      diagnosisState == null ||
      diagnosisState.diagnosisStatusDiv == DivConst.items.diagnosisStatus.unpublished.value
    );
  };

  /* ----------------------------------------------------------------------- */
  /* 公開関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 未稼働かどうかを判定します（公開向け）。
   */
  const isUnpublished = (): boolean => _isUnpublished();

  /**
   * 読み取り専用かどうかを判定します。
   */
  const isReadOnly = (): boolean => {
    return (
      diagnosisState != null &&
      diagnosisState.diagnosisStatusDiv != DivConst.items.diagnosisStatus.unpublished.value
    );
  };

  /* ----------------------------------------------------------------------- */
  /* 戻り値 */
  /* ----------------------------------------------------------------------- */
  return {
    useFormMethods,
    questionFieldArray,
    resultFieldArray,
    respondentFieldArray,
    initialized,
    processingOnSave,
    processingOnPreview,
    processingOnPublish,
    processingOnPublishTerminate,
    processingOnReserveCancel,
    processingOnDelete,
    processingOnCreateQuestion,
    diagnosisState,
    isExpiredPlan,
    canPublished,
    isUnpublished,
    isReadOnly,
    modalPublishOpenState,
    setModalPublishOpenState,
    modalConfirmOpenState,
    setModalConfirmOpenState,
    modalConfirmTitle,
    onSave,
    onPreview,
    onPublishModalOpen,
    onConfirmModalSubmit,
    onPublishModalSubmit,
    onPublishTerminate,
    onReserveCancel,
    onDelete,
    onCreateDiagnosisRef,
    navigate,
    confirm,
  };
}
